import React from 'react';
import { observer } from 'mobx-react';

import { IBarProps, IChartData } from '../../../models/charting/index.js';
import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import ChartBase from '../ChartBase/ChartBase.js';
import { getChartBaseProps, useAxisDateAsNumber } from '../ChartBase/ChartBaseFunctions.js';
import { IChartBaseProps, ISingleChart } from '../ChartBase/models/index.js';

import { IBarChartConfiguration, IBarChartProps, IBarChartState } from './models/index.js';
import Wrapper from './styledComponents/Wrapper.js';
import { BAR_CHART_CATEGORY_KEY, BAR_CHART_COMPONENT_KEY } from './BarChart.meta.js';

export class BarChart extends ComponentBase<IBarChartProps, IBarChartState, IBarChartConfiguration> {
	categoryKey = BAR_CHART_CATEGORY_KEY;
	componentKey = BAR_CHART_COMPONENT_KEY;

	constructor(props: IBarChartProps) {
		super(props);
	}

	get chartBaseProps(): IChartBaseProps {
		const { dateOptions, breakpoints } = this.props;
		let data = this.props.data;
		// Attempt to manipulate the data based on the base props
		data = this.applyAxisDateAsNumber(this.props, data);

		// If the base props does not have dateOptions set and breakpoints are configured
		// See if there is dateOptions set in them
		if (!dateOptions && breakpoints) {
			// We dont need to manipulate the data for each breakpoint
			// as both axis will reference the same dataKey
			const dateOptionsBreakpoint = breakpoints.find((breakpoint) => {
				return breakpoint.props && breakpoint.props.dateOptions;
			});
			if (dateOptionsBreakpoint) {
				const { props } = dateOptionsBreakpoint;
				if (props) {
					data = this.applyAxisDateAsNumber(props, data);
				}
			}
		}
		return getChartBaseProps({ type: 'Bar' }, this.configuration, { ...this.props, data });
	}

	applyAxisDateAsNumber(props: ISingleChart<IBarProps>, data?: IChartData[]): IChartData[] | undefined {
		const { elementOverrides, dateOptions } = props;
		if (dateOptions) {
			if (elementOverrides) {
				const { xAxis, yAxis } = elementOverrides;
				if (xAxis && dateOptions.xAxisDateAsNumber) {
					data = useAxisDateAsNumber(xAxis, data);
				}
				if (yAxis && dateOptions.yAxisDateAsNumber) {
					data = useAxisDateAsNumber(yAxis, data);
				}
			} else {
				console.warn(
					'No elementOverrides specified when trying to use dateOptions. dateOptions will not be applied',
				);
			}
		}
		return data;
	}

	render() {
		const { className } = this.props;
		return (
			<Wrapper className={className}>
				<ChartBase {...this.chartBaseProps} />
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(BarChart), BAR_CHART_CATEGORY_KEY, BAR_CHART_COMPONENT_KEY);
